import { ReportSectionStyled } from '@/reports/shared/ReportSection/ReportSection';
import { serializedTypes } from '@/reports/types';
import { styled, Stack, Text, Box, Button, Card } from '@/theme/index';
import { scrollToElement } from '@/utils/scrollToElement';

const CallerGif = styled(Box)<{ imageurl: string }>`
  width: 150px;
  height: 150px;
  margin: 0 auto;
  background-size: 150px;
  background-image: url(${(p) => p.imageurl});
`;

const CopyWrapper = styled(Box)`
  position: relative;
  top: 55px;
  margin: 0 auto;
  width: 70%;
`;

const GifText = styled(Text)(({ theme }) => ({
  color: theme.palette.primary.dark,
  textAlign: 'center',
  fontSize: '14px',
  fontWeight: '600',
}));

const getCallerType = (spamScore: number) => {
  if (spamScore >= 50) {
    return 'telemarketer';
  }
  if (spamScore >= 10 && spamScore < 50) {
    return 'questionable';
  }
  return 'legit';
};

const GifTextWrapper = ({
  callerType,
}: {
  callerType: string;
}): JSX.Element => {
  if (callerType === 'telemarketer') {
    return (
      <>
        <GifText>Unwanted</GifText>
        <GifText>Caller</GifText>
      </>
    );
  }
  if (callerType === 'questionable') {
    return (
      <>
        <GifText>Questionable</GifText>
        <GifText>Caller</GifText>
      </>
    );
  }
  return <GifText>No Complaints Found</GifText>;
};

type CallerTypes = 'telemarketer' | 'questionable' | 'legit';

type Props = {
  reportData: serializedTypes.SerializedPhoneReport;
  callerSourceMap: { [key in CallerTypes]: string };
};

export function NuisanceCaller({ reportData, callerSourceMap }: Props) {
  const { spam_score: spamScore, spam_reports_count: spamReportsCount } =
    reportData;

  const callerType = getCallerType(spamScore);
  const callerGifSrc = callerSourceMap[callerType];
  let nuisanceCallLabel;

  if (spamScore >= 50) {
    nuisanceCallLabel = 'Unwanted Caller';
  } else if (spamReportsCount) {
    nuisanceCallLabel = 'Complaints Found';
  } else {
    nuisanceCallLabel = 'No Complaints';
  }

  const shouldShowNuisanceComplaints = spamScore >= 3;
  const label = 'View Comments';

  return (
    <ReportSectionStyled id="nuisance-caller-section">
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2.5}
      >
        <CallerGif imageurl={callerGifSrc}>
          <CopyWrapper>
            <GifTextWrapper callerType={callerType} />
          </CopyWrapper>
        </CallerGif>
        <Box textAlign="center">
          <Text variant="h1">
            Nuisance call scan detection: {nuisanceCallLabel}
          </Text>
          <Text>
            We identify spammers and telemarketers using user-submitted feedback
            about a phone number
          </Text>
        </Box>
        {shouldShowNuisanceComplaints && (
          <Card variant="outlined" sx={{ width: '100%' }}>
            <Stack alignItems="center" justifyContent="center">
              <Text>This number has had </Text>
              <Text fontSize="30px">{`${spamReportsCount} complaints`}</Text>
            </Stack>
          </Card>
        )}
        {!!label && (
          <Button
            color="success"
            variant="contained"
            sx={{
              whiteSpace: 'nowrap',
              width: { xs: '100%', md: 'unset' },
              paddingBlock: 0.75,
              paddingInline: 3.3125,
            }}
            onClick={() => scrollToElement('comments-section')}
          >
            {label}
          </Button>
        )}
      </Stack>
    </ReportSectionStyled>
  );
}
